// i18n bootstrap — FR + EN, namespaces lazy-grouped.
// Detection chain: localStorage('lg_locale') -> navigator.language -> 'en'.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon from "./locales/en/common.json";
import enAuth from "./locales/en/auth.json";
import enDashboard from "./locales/en/dashboard.json";
import enDocuments from "./locales/en/documents.json";
import enUpload from "./locales/en/upload.json";
import enAnalysis from "./locales/en/analysis.json";
import enChat from "./locales/en/chat.json";
import enAlerts from "./locales/en/alerts.json";
import enComponents from "./locales/en/components.json";
import enCrosscheck from "./locales/en/crosscheck.json";
import enUnderWatch from "./locales/en/under_watch.json";
import enExecutiveReport from "./locales/en/executive_report.json";
import enVigilanceReports from "./locales/en/vigilance_reports.json";
import enInvoices from "./locales/en/invoices.json";
import enTrash from "./locales/en/trash.json";
import enActivity from "./locales/en/activity.json";
import enAdmin from "./locales/en/admin.json";
import enHome from "./locales/en/home.json";
import enLegal from "./locales/en/legal.json";
import enWorkspaces from "./locales/en/workspaces.json";
import enManualFinancials from "./locales/en/manual_financials.json";
import enTours from "./locales/en/tours.json";
import enHelp from "./locales/en/help.json";
import enSuggestions from "./locales/en/suggestions.json";
import enPreSignature from "./locales/en/pre_signature.json";

import frCommon from "./locales/fr/common.json";
import frAuth from "./locales/fr/auth.json";
import frDashboard from "./locales/fr/dashboard.json";
import frDocuments from "./locales/fr/documents.json";
import frUpload from "./locales/fr/upload.json";
import frAnalysis from "./locales/fr/analysis.json";
import frChat from "./locales/fr/chat.json";
import frAlerts from "./locales/fr/alerts.json";
import frComponents from "./locales/fr/components.json";
import frCrosscheck from "./locales/fr/crosscheck.json";
import frUnderWatch from "./locales/fr/under_watch.json";
import frExecutiveReport from "./locales/fr/executive_report.json";
import frVigilanceReports from "./locales/fr/vigilance_reports.json";
import frInvoices from "./locales/fr/invoices.json";
import frTrash from "./locales/fr/trash.json";
import frActivity from "./locales/fr/activity.json";
import frAdmin from "./locales/fr/admin.json";
import frHome from "./locales/fr/home.json";
import frLegal from "./locales/fr/legal.json";
import frWorkspaces from "./locales/fr/workspaces.json";
import frManualFinancials from "./locales/fr/manual_financials.json";
import frTours from "./locales/fr/tours.json";
import frHelp from "./locales/fr/help.json";
import frSuggestions from "./locales/fr/suggestions.json";
import frPreSignature from "./locales/fr/pre_signature.json";

export const SUPPORTED_LOCALES = ["en", "fr"];
export const DEFAULT_LOCALE = "en";

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    documents: enDocuments,
    upload: enUpload,
    analysis: enAnalysis,
    chat: enChat,
    alerts: enAlerts,
    components: enComponents,
    crosscheck: enCrosscheck,
    under_watch: enUnderWatch,
    executive_report: enExecutiveReport,
    vigilance_reports: enVigilanceReports,
    invoices: enInvoices,
    trash: enTrash,
    activity: enActivity,
    admin: enAdmin,
    home: enHome,
    legal: enLegal,
    workspaces: enWorkspaces,
    manual_financials: enManualFinancials,
    tours: enTours,
    help: enHelp,
    suggestions: enSuggestions,
    pre_signature: enPreSignature,
  },
  fr: {
    common: frCommon,
    auth: frAuth,
    dashboard: frDashboard,
    documents: frDocuments,
    upload: frUpload,
    analysis: frAnalysis,
    chat: frChat,
    alerts: frAlerts,
    components: frComponents,
    crosscheck: frCrosscheck,
    under_watch: frUnderWatch,
    executive_report: frExecutiveReport,
    vigilance_reports: frVigilanceReports,
    invoices: frInvoices,
    trash: frTrash,
    activity: frActivity,
    admin: frAdmin,
    home: frHome,
    legal: frLegal,
    workspaces: frWorkspaces,
    manual_financials: frManualFinancials,
    tours: frTours,
    help: frHelp,
    suggestions: frSuggestions,
    pre_signature: frPreSignature,
  },
};

// Reduce any locale like "fr-CA" / "fr_FR" down to "fr".
function normalizeLocale(raw) {
  if (!raw) return DEFAULT_LOCALE;
  const short = String(raw).toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LOCALES.includes(short) ? short : DEFAULT_LOCALE;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    nonExplicitSupportedLngs: true,
    defaultNS: "common",
    ns: ["common", "auth", "dashboard", "documents", "upload", "analysis", "chat", "alerts", "components", "crosscheck", "under_watch", "executive_report", "vigilance_reports", "invoices", "trash", "activity", "admin", "home", "legal", "workspaces", "manual_financials", "tours", "help", "suggestions", "pre_signature"],
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "lg_locale",
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

// Force-normalize to a supported locale (in case detector picked "de" etc.)
if (!SUPPORTED_LOCALES.includes(i18n.language)) {
  i18n.changeLanguage(normalizeLocale(i18n.language));
}

export function setLocale(locale) {
  const next = normalizeLocale(locale);
  i18n.changeLanguage(next);
  try {
    localStorage.setItem("lg_locale", next);
  } catch (_) { /* ignore */ }
  return next;
}

export function getLocale() {
  return normalizeLocale(i18n.language);
}

export { normalizeLocale };
export default i18n;
